import styled from 'styled-components';
import theme from 'styled-theming';
import { Modal as ShellModal } from 'src/UI/Modal';

const marketHeaderBackground = theme('name', {
  dark: 'rgb(42, 48, 55)',
  light: 'rgb(185, 185, 185)',
});

const gridBorder = theme('name', {
  dark: 'rgba(255, 255, 255, 0.14)',
  light: 'rgba(17, 24, 39, 0.24)',
});

const cellText = theme('name', {
  dark: 'rgb(244, 248, 252)',
  light: 'rgb(30, 36, 43)',
});

const headerEvenBackground = theme('name', {
  dark: 'rgb(61, 61, 61)',
  light: 'rgb(233, 237, 243)',
});

const headerOddBackground = theme('name', {
  dark: 'rgb(52, 52, 52)',
  light: 'rgb(224, 229, 236)',
});

export const MarketPositionNewContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  padding: 0;

  > * {
    flex-shrink: 0;
  }

  .filter-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    row-gap: 12px;
    margin-bottom: 16px;
  }

  .filter-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .period-filter-field {
    width: 130px;
    margin-right: 16px;
  }

  .gas-day-controls {
    display: flex;
    align-items: flex-end;
    gap: 4px;

    > button {
      background: ${(props) => props.theme.background.surface};
    }
  }

  .gas-day-picker {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .gas-day {
    width: 120px;
  }

  .search-filter-field {
    width: 240px;
    margin-left: 16px;
  }

  .legal-entity-filter-field {
    width: 360px;
    flex-shrink: 0;
  }

  .custom-view-filter-field {
    width: auto;
    max-width: calc(100% - 16px);
    flex-shrink: 0;
    margin-left: auto;
    padding-left: 16px;
  }

  .custom-view-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;

    .shell-select,
    .shell-text-input {
      flex: 0 1 320px;
      width: 320px;
      min-width: 0;
    }

    > button,
    .custom-view-public {
      flex-shrink: 0;
    }
  }

  .search-container {
    min-width: 280px;
    margin-left: 16px;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .search-input-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .shell-text-input {
      flex: 1;
      min-width: 0;
    }

    div[type='suffix'] {
      padding: 0;
      margin: 0;
    }

    .search-apply-btn {
      min-height: unset;
      height: 32px;
      color: rgb(28, 28, 28);

      svg {
        color: rgb(28, 28, 28) !important;
      }

      svg path {
        fill: rgb(28, 28, 28) !important;
        stroke: rgb(28, 28, 28) !important;
      }
    }

    .search-clear-btn {
      height: 32px;
      min-height: 32px;
      margin: 0;
      white-space: nowrap;
    }

    .search-validation-message {
      color: ${(props) => props.theme.text.error ?? '#c0392b'};
      font-size: 11px;
    }
  }

  .market-refresh-button {
    margin-left: 12px;
  }

  .market-button-group {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
    margin: 0 0 16px;
    padding: 0;
    border: 0;
  }

  .market-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    min-height: 28px;
    padding: 4px 10px;
    border: 1px solid transparent;
    border-radius: 4px;
    background: ${(props) => props.theme.background.surface};
    color: ${cellText};
    font: inherit;
    font-size: 12px;
    cursor: pointer;

    &[aria-pressed='true'] {
      background: #0085a8;
      color: #fff;
    }

    &:hover,
    &:focus-visible {
      border-color: #0085a8;
    }
  }

  .hierarchy-badge {
    display: inline-flex;
    flex: 0 0 20px;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: 0;
  }

  .hierarchy-badge-nomination-group {
    flex-basis: 20px;
    width: 20px;
    font-size: 9px;
    background: #2f8f46;
  }

  .hierarchy-badge-market {
    background: #ed5564;
  }

  .hierarchy-badge-contract {
    background: #b8860b;
  }

  .hierarchy-badge-location {
    background: #7048b8;
  }

  .hierarchy-badge-shipper {
    background: #079aaa;
  }

  .hierarchy-badge-position {
    background: #c87500;
  }

  .filter-label {
    color: ${(props) => props.theme.text.medium};
    font-size: 12px;
    font-weight: 600;
  }

  .table-area {
    position: relative;
    isolation: isolate;
    display: flex;
    flex: 0 1 auto;
    flex-direction: column;
    min-height: 0;

    > output {
      display: block;
    }
  }

  .table-focus-toolbar {
    position: absolute;
    top: 2px;
    right: 16px;
    z-index: 1;
    display: flex;

    > button {
      background: #fbce07;
      color: #1c1c1c;
      border-color: #1c1c1c;

      &:hover,
      &:focus-visible {
        background: #ffe66b;
        color: #1c1c1c;
        border-color: #1c1c1c;
      }
    }
  }

  .time-series-wrapper {
    position: relative;
    isolation: isolate;
    flex: 0 1 auto;
    width: 100%;
    min-height: 0;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    border: 1px solid ${(props) => props.theme.border.medium};
    border-radius: 6px;
    background: ${(props) => props.theme.background.surface};

    &::-webkit-scrollbar {
      width: 14px;
      height: 14px;
    }

    &::-webkit-scrollbar-track {
      background: ${(props) => props.theme.background.base};
    }

    &::-webkit-scrollbar-thumb {
      background: ${(props) => props.theme.border.medium};
      border: 3px solid ${(props) => props.theme.background.base};
      border-radius: 8px;
    }
  }

  &.table-focused {
    > .filter-row,
    > .market-button-group {
      display: none;
    }
  }

  .time-series-table {
    width: max-content;
    min-width: 100%;
    border-collapse: separate;
    border-spacing: 0;

    th,
    td {
      min-width: 122px;
      padding: 4px 8px;
      border-right: 1px solid ${gridBorder};
      border-bottom: 1px solid ${gridBorder};
      box-sizing: border-box;
      color: ${cellText};
      font-size: 12px;
      overflow: hidden;
      text-align: right;
      text-overflow: ellipsis;
      white-space: nowrap;
      background: ${(props) => props.theme.background.surface};
    }

    th {
      position: sticky;
      top: 0;
      z-index: 3;
      background: ${(props) => props.theme.background.raised};
      font-weight: 600;
      height: 40px;
      line-height: 1.2;
      text-align: center;
    }

    thead th:nth-child(even) {
      background: ${headerEvenBackground};
    }

    thead th:nth-child(odd) {
      background: ${headerOddBackground};
    }

    thead th.tree-column,
    thead th.volume-type-column,
    thead th.selection-column,
    thead th.total-column {
      background: ${(props) => props.theme.background.raised};
    }

    tbody tr.market-header-row td {
      background: ${marketHeaderBackground};
      font-weight: 600;
    }

    tbody tr.market-header-row td.total-column {
      font-weight: 700;
    }

    tbody tr.market-header-row:not(:first-child) td {
      border-top: 10px solid ${(props) => props.theme.background.base};
    }

    .tree-column {
      position: sticky;
      left: 0;
      z-index: 4;
      width: 360px;
      min-width: 360px;
      max-width: 360px;
      text-align: left;
    }

    thead .tree-column {
      z-index: 5;
    }

    .volume-type-column {
      width: 110px;
      min-width: 110px;
      text-align: center;
    }

    .selection-column {
      width: 40px;
      min-width: 40px;
      max-width: 40px;
      padding: 4px;
      text-align: center;

      .selection-checkbox {
        margin-left: 4px;
      }
    }

    .total-column {
      min-width: 160px;
    }

    tbody .total-column {
      font-weight: 700;
    }

    .tree-cell {
      display: flex;
      align-items: center;
      gap: 6px;
      min-height: 32px;
      padding: 6px 12px;
      box-sizing: border-box;
      color: ${(props) => props.theme.text.onSurface};
      overflow: hidden;
    }

    .tree-cell-context-target {
      display: flex;
      align-items: center;
      flex: 1;
      min-width: 0;
      margin: 0;
      padding: 0;
      border: 0;
      background: none;
      font: inherit;
      color: inherit;
      text-align: left;
      cursor: pointer;
    }

    .tree-node-name {
      display: inline-flex !important;
      flex: 1;
      align-items: center;
      gap: 7px;
      min-width: 0;
    }

    .tree-node-name > svg,
    .tree-node-name .hierarchy-badge {
      flex-shrink: 0;
    }

    .tree-node-label {
      display: block;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .tree-cell > span {
      display: block;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      font-size: 13px;
      line-height: 1.25;
      white-space: nowrap;
      color: inherit;
    }

    .tree-loading-cell {
      color: ${(props) => props.theme.text.medium};
      font-style: italic;
    }

    .tree-toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      padding: 0;
      border: 0;
      background: transparent;
      color: inherit;
      cursor: pointer;
    }

    .tree-toggle:disabled {
      cursor: default;
    }

    .empty-message {
      margin-top: 24px;
      color: ${(props) => props.theme.text.medium};
      font-size: 14px;
    }
  }
`;

export const ActionMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${(props) => props.$top}px;
  left: ${(props) => props.$left}px;
  z-index: 1000;
  box-shadow:
    rgba(0, 0, 0, 0.2) 0 2px 5px,
    rgba(0, 0, 0, 0.2) 0 0 2px;
  border: 1px solid rgb(221, 221, 221);
  border-radius: 4px;
  background: ${(props) => props.theme.background.raised};

  .menu-container {
    display: flex;
    flex-direction: column;
    padding: 3px 5px;
  }
`;

export const SearchValidationModal = styled(ShellModal)`
  .button-style {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 10px;
  }
  .shell-modal-container-content {
    min-height: unset;
    margin-bottom: 16px;
  }
`;

export const JobsModal = styled(ShellModal)`
  .jobs-actions {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 8px;
  }

  .shell-table {
    th {
      padding: 8px;
    }

    td:not(.no-data-cell) {
      padding: 8px;
    }

    th:first-child,
    td:first-child:not(.no-data-cell) {
      width: 1%;
      white-space: nowrap;
      padding-left: 8px;
      padding-right: 8px;
    }

    .job-table-actions-cell {
      display: inline-flex;
      align-items: center;
      gap: 4px;

      .shell-button {
        padding: 0;
        min-width: unset;
        width: 24px;
        height: 24px;
      }

      .job-status-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        margin-left: 2px;
      }
    }
  }

  .button-style {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding: 10px;
  }

  .jobs-empty-state {
    padding: 24px;
    color: ${(props) => props.theme.text.medium};
    text-align: center;
  }

  .jobs-loader {
    display: flex;
    justify-content: center;
    padding: 40px;
  }

  .job-details {
    padding: 0 4px;
    h3,
    h4 {
      margin: 0;
    }

    .job-details-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 24px;
      padding-top: 16px;
      border-top: 1px solid ${(props) => props.theme.border.medium};
    }

    .job-details-header {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      gap: 4px;
      margin-bottom: 16px;
    }

    h4 {
      margin-bottom: 12px;
    }
  }

  .job-details-layout {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 32px;
  }

  .job-details-column + .job-details-column {
    padding-left: 32px;
    border-left: 1px solid ${(props) => props.theme.border.medium};
  }

  .job-detail-section + .job-detail-section {
    margin-top: 24px;
  }

  .job-details-fields {
    display: grid;
    gap: 12px;
    min-width: 0;
  }

  .job-details-fields .form-error-message {
    margin-top: -12px;
  }

  .extended-properties-fields .form-error-message {
    margin-top: 0;
  }

  .job-detail-item {
    display: grid;
    grid-template-columns: minmax(130px, 0.7fr) minmax(0, 1.3fr);
    gap: 12px;
    align-items: start;
    padding: 5px 0;
  }

  .job-detail-label {
    font-weight: 600;
  }

  .job-detail-value {
    overflow-wrap: anywhere;
  }

  @media (max-width: 700px) {
    .job-details-layout {
      grid-template-columns: 1fr;
      gap: 24px;
    }

    .job-details-column + .job-details-column {
      padding-left: 0;
      border-left: 0;
    }
  }
`;
